import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder.js';
import { NOTE_TEMPLATES } from '../lib/noteTemplates.js';
import {
  enqueue, requestSync, flushPending, listPending, remove as removeQueued,
  blobFor, fileNameFor, mirrorAuthToken, MAX_ATTEMPTS,
} from '../lib/voiceQueue.js';
import SearchableSelect from '../components/SearchableSelect.jsx';
import clsx from 'clsx';

const ADMIN_ROLES = ['superadmin', 'dealer_admin', 'branch_admin'];

export default function NoteCapture() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [params] = useSearchParams();
  const initialCustomerId = params.get('customer_id') ? parseInt(params.get('customer_id'), 10) : null;
  const followUpId = params.get('follow_up_id') ? parseInt(params.get('follow_up_id'), 10) : null;
  const [customerId, setCustomerId] = useState(initialCustomerId);
  const [customerNameHint, setCustomerNameHint] = useState('');
  const [repUserId, setRepUserId] = useState(''); // admin-only: attribute note to a rep
  const [contactName, setContactName] = useState('');
  const [mode, setMode] = useState('voice'); // 'voice' | 'typed'
  const [text, setText] = useState('');
  const [touchType, setTouchType] = useState(params.get('touch_type') || 'note');
  const [domain, setDomain] = useState(params.get('domain') || '');
  const [visibility, setVisibility] = useState('team');
  const [queued, setQueued] = useState([]);
  const pendingCount = queued.length;
  const [retryingQueue, setRetryingQueue] = useState(false);
  const [retryQueueError, setRetryQueueError] = useState(null);
  const [savedOffline, setSavedOffline] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [noteDate, setNoteDate] = useState(today);
  const [dateConfirmed, setDateConfirmed] = useState(false);
  const isBackdated = noteDate !== today;
  const rec = useVoiceRecorder();

  const linkedFollowUp = useQuery({
    queryKey: ['followup', followUpId],
    queryFn: () => api.get(`/followups/${followUpId}`).then(r => r.data.data),
    enabled: !!followUpId,
  });

  const salespersons = useQuery({
    queryKey: ['opportunities', 'salespersons'],
    queryFn: () => api.get('/opportunities/salespersons').then(r => r.data.data),
    enabled: isAdmin,
  });

  const refreshQueue = useCallback(() => {
    listPending().then(setQueued).catch(() => {});
  }, []);

  // On mount: read the existing queue. On 'online': flush and reload.
  // Mirroring the live token on mount is what lets the service worker replay
  // with a current JWT rather than the one frozen in at record time.
  useEffect(() => {
    mirrorAuthToken().catch(() => {});
    refreshQueue();
    const handleOnline = async () => {
      try { await flushPending(); } catch {}
      refreshQueue();
    };
    // Also listen for the SW telling us uploads completed (Background Sync path)
    const handleSwMessage = (e) => {
      if (e.data?.type === 'VOICE_UPLOADS_FLUSHED') refreshQueue();
    };
    window.addEventListener('online', handleOnline);
    navigator.serviceWorker?.addEventListener('message', handleSwMessage);
    return () => {
      window.removeEventListener('online', handleOnline);
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
    };
  }, [refreshQueue]);

  // Manual retry — the 'online' event listener above is the primary path, but
  // iOS Safari has no Background Sync API, so a queued recording only retries
  // when a real network state change fires. If the connection never actually
  // dropped (e.g. it was a server-side error, not a client network issue),
  // that event never fires and the recording sits queued indefinitely. This
  // gives the rep a way to force a retry without toggling airplane mode.
  async function retryQueueNow() {
    setRetryingQueue(true);
    setRetryQueueError(null);
    try {
      // includeFailed: an explicit tap is the rep saying "try it anyway" —
      // give parked entries one more real chance (e.g. after a fresh sign-in).
      const res = await flushPending(undefined, { includeFailed: true });
      if (res.failures.length) {
        setRetryQueueError(
          res.uploaded > 0
            ? `Uploaded ${res.uploaded}; ${res.failures.length} still queued.`
            : `Still queued — ${res.failures[0].message}`
        );
      }
    } catch (err) {
      setRetryQueueError(err?.message || 'Could not reach the server.');
    } finally {
      refreshQueue();
      setRetryingQueue(false);
    }
  }

  // Reasons a queued recording can be rejected, in the rep's language. The
  // whole point of this screen is that a stuck recording says WHY it is stuck
  // and offers a way out — the previous version showed one generic line and
  // retried a doomed upload forever with no way to inspect or clear it.
  function queueStatusText(entry) {
    if (entry.lastStatus === 401 || entry.lastStatus === 403) {
      return 'Sign-in expired — sign out and back in, then tap Retry.';
    }
    if (entry.lastStatus === 400) return entry.lastError || 'The recording did not come through.';
    if (entry.lastStatus === 429) return 'Too many notes at once — retry in a few minutes.';
    if (entry.lastStatus) return entry.lastError || `Rejected (${entry.lastStatus}).`;
    if (entry.attempts > 0) return 'Waiting for a connection.';
    return 'Waiting to upload.';
  }

  async function discardQueued(id) {
    await removeQueued(id).catch(() => {});
    refreshQueue();
  }

  function downloadQueued(entry) {
    const url = URL.createObjectURL(blobFor(entry));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileNameFor(entry);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  const saveTyped = useMutation({
    mutationFn: (data) => api.post('/notes', data).then(r => r.data.data),
    onSuccess: (n) => nav(`/notes/${n.id}`),
  });

  const saveVoice = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('audio', rec.blob, `note-${Date.now()}.webm`);
      if (customerId) fd.append('customer_id', String(customerId));
      else if (customerNameHint) fd.append('customer_name_hint', customerNameHint);
      fd.append('duration_sec', String(rec.durationSec));
      fd.append('touch_type', touchType);
      if (domain) fd.append('domain', domain);
      fd.append('visibility', visibility);
      fd.append('note_date', noteDate);
      if (followUpId) fd.append('follow_up_id', String(followUpId));
      try {
        const r = await api.post('/notes/voice', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        return { queued: false, note: r.data.data };
      } catch (err) {
        // No response = network error (offline). Queue for background sync.
        if (!err.response) {
          await enqueue({
            audioBlob: rec.blob,
            mimeType: rec.blob.type,
            customerId,
            customerNameHint,
            touchType,
            domain,
            visibility,
            durationSec: rec.durationSec,
            noteDate,
            followUpId,
          });
          await requestSync();
          return { queued: true };
        }
        throw err;
      }
    },
    onSuccess: (result) => {
      if (result.queued) {
        refreshQueue();
        setSavedOffline(true);
        rec.reset();
      } else {
        nav(`/notes/${result.note.id}`);
      }
    },
  });

  const submitting = saveTyped.isPending || saveVoice.isPending;
  const dateBlocked = isBackdated && !dateConfirmed;

  return (
    <div className="space-y-4 max-w-xl mx-auto">
      <header>
        <div className="section-eyebrow">Capture</div>
        <h1 className="text-2xl">New Note</h1>
        <div className="rule-crimson" />
      </header>

      {followUpId && (
        <div className="rounded border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900 flex items-center gap-2">
          <span>📋</span>
          <span>
            {linkedFollowUp.data
              ? <>Note for follow-up: <span className="font-semibold">{linkedFollowUp.data.title}</span></>
              : 'Adding note for follow-up…'}
          </span>
        </div>
      )}

      {pendingCount > 0 && (
        <div className="rounded bg-amber-50 border border-amber-200 text-amber-900 text-sm px-3 py-2">
          <div className="flex items-center gap-2 justify-between">
            <span className="flex items-center gap-2">
              <span>⏳</span>
              <span>{pendingCount} recording{pendingCount > 1 ? 's' : ''} queued — will upload automatically when connection returns.</span>
            </span>
            <button
              type="button"
              className="btn-ghost text-xs px-2 py-1 whitespace-nowrap shrink-0"
              onClick={retryQueueNow}
              disabled={retryingQueue}
            >
              {retryingQueue ? 'Retrying…' : 'Retry now'}
            </button>
          </div>
          {retryQueueError && <div className="text-crimson text-xs mt-1">{retryQueueError}</div>}

          <ul className="mt-2 space-y-1 border-t border-amber-200 pt-2">
            {queued.map(entry => (
              <li key={entry.id} className="text-xs flex items-start gap-2 justify-between">
                <span className="min-w-0">
                  <span className="font-semibold">
                    {entry.queuedAt ? new Date(entry.queuedAt).toLocaleString() : 'Recording'}
                  </span>
                  {entry.durationSec ? <span className="opacity-70"> · {entry.durationSec}s</span> : null}
                  <span className={clsx('block', entry.state === 'failed' ? 'text-crimson' : 'opacity-80')}>
                    {queueStatusText(entry)}
                    {entry.attempts > 0 && (
                      <span className="opacity-70"> ({entry.attempts}/{MAX_ATTEMPTS} attempts)</span>
                    )}
                  </span>
                </span>
                <span className="flex gap-1 shrink-0">
                  <button
                    type="button"
                    className="btn-ghost text-xs px-2 py-0.5"
                    onClick={() => downloadQueued(entry)}
                    title="Save this recording to your device"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn-ghost text-xs px-2 py-0.5 text-crimson"
                    onClick={() => discardQueued(entry.id)}
                    title="Remove this recording from the queue"
                  >
                    Discard
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <label className="label">Customer (optional)</label>
          <CustomerCombobox
            value={customerId}
            hintValue={customerNameHint}
            onChange={(id, name, isHint) => {
              if (isHint) { setCustomerId(null); setCustomerNameHint(name); }
              else { setCustomerId(id); setCustomerNameHint(''); }
            }}
          />
        </div>
        <div>
          <label className="label">Touch type</label>
          <select className="input" value={touchType} onChange={e => setTouchType(e.target.value)}>
            <option value="note">Note</option>
            <option value="visit">Visit</option>
            <option value="call">Call</option>
            <option value="email">Email</option>
            <option value="quote">Quote</option>
          </select>
        </div>
        <div>
          <label className="label">Department (optional)</label>
          <select className="input" value={domain} onChange={e => setDomain(e.target.value)}>
            <option value="">— Unspecified —</option>
            <option value="parts">Parts</option>
            <option value="service">Service</option>
            <option value="trailer_sales">Trailer sales</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div>
        <label className="label">Activity date</label>
        <input
          className="input"
          type="date"
          value={noteDate}
          max={today}
          onChange={e => { setNoteDate(e.target.value); setDateConfirmed(false); }}
        />
      </div>

      {isBackdated && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <div className="font-medium mb-1">This note will be dated {new Date(noteDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}.</div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={dateConfirmed} onChange={e => setDateConfirmed(e.target.checked)} />
            <span>Yes, that date is correct</span>
          </label>
        </div>
      )}

      {isAdmin && (
        <div>
          <label className="label">Assign to rep (optional)</label>
          <SearchableSelect
            options={(salespersons.data ?? []).map(s => ({ value: s.id, label: s.full_name }))}
            value={repUserId}
            onChange={setRepUserId}
            placeholder="My own note (default)"
            emptyLabel="— My own note —"
          />
        </div>
      )}

      {touchType === 'call' && (
        <div>
          <label className="label">Contact name <span className="text-crimson">*</span></label>
          <input
            className="input"
            placeholder="Person you spoke with"
            value={contactName}
            onChange={e => setContactName(e.target.value)}
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2">
          <button onClick={() => { setMode('voice'); setSavedOffline(false); }} className={clsx('btn px-3 py-1', mode === 'voice' ? 'bg-navy text-white' : 'bg-panel text-navy')}>🎙 Voice</button>
          <button onClick={() => setMode('typed')} className={clsx('btn px-3 py-1', mode === 'typed' ? 'bg-navy text-white' : 'bg-panel text-navy')}>⌨ Typed</button>
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={visibility === 'team'}
                 onChange={e => setVisibility(e.target.checked ? 'team' : 'private')} />
          <span className="text-navy">{visibility === 'team' ? '👥 Visible to team' : '🔒 Private to me'}</span>
        </label>
      </div>

      {mode === 'voice' ? (
        <div className="card p-6 text-center">
          {rec.error && <div className="text-crimson text-sm mb-2">{rec.error}</div>}

          {savedOffline && !rec.blob && (
            <div className="text-amber-700 text-sm mb-4">
              Saved offline — will upload when connection returns.
              <button className="ml-2 underline text-amber-900" onClick={() => setSavedOffline(false)}>Record another</button>
            </div>
          )}

          {!rec.blob && !savedOffline && (
            <>
              <button
                onClick={rec.recording ? rec.stop : rec.start}
                className={clsx(
                  'rounded-full w-28 h-28 mx-auto text-5xl shadow-panel transition',
                  rec.recording ? 'bg-crimson text-white animate-pulse' : 'bg-navy text-white hover:bg-navy-600'
                )}
              >
                {rec.recording ? '■' : '●'}
              </button>
              <div className="mt-3 text-sm text-muted">
                {rec.recording ? `Recording… ${formatDuration(rec.durationSec)}` : 'Tap to start'}
              </div>
            </>
          )}
          {rec.blob && (
            <>
              <audio controls src={URL.createObjectURL(rec.blob)} className="w-full mt-2" />
              <div className="text-sm text-muted mt-2">{formatDuration(rec.durationSec)} · {(rec.blob.size / 1024).toFixed(0)} KB</div>
              <div className="flex justify-center gap-2 mt-4">
                <button className="btn-ghost" onClick={() => { rec.reset(); setSavedOffline(false); }}>Re-record</button>
                <button className="btn-primary" disabled={submitting || dateBlocked} onClick={() => saveVoice.mutate()}>
                  {submitting ? 'Saving…' : 'Save & transcribe'}
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div>
            <label className="label">Template (optional)</label>
            <select className="input"
                    onChange={e => {
                      const t = NOTE_TEMPLATES.find(x => x.id === e.target.value);
                      if (t) {
                        setText(t.prompt);
                        if (t.touch_type) setTouchType(t.touch_type);
                      }
                      e.target.value = '';
                    }}
                    defaultValue="">
              <option value="">— Pick a template to prefill —</option>
              {NOTE_TEMPLATES.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <textarea
            className="input min-h-[240px]"
            placeholder="Type the note. It will be structured automatically…"
            value={text}
            onChange={e => setText(e.target.value)}
            spellCheck={true}
            autoCorrect="on"
            autoCapitalize="sentences"
          />
          <div className="flex justify-end mt-2">
            <button
              className="btn-primary"
              disabled={!text.trim() || submitting || dateBlocked || (touchType === 'call' && !contactName.trim())}
              onClick={() => saveTyped.mutate({
                transcript: text,
                ...(customerId ? { customer_id: customerId } : {}),
                ...(customerNameHint ? { customer_name_hint: customerNameHint } : {}),
                touch_type: touchType,
                ...(domain ? { domain } : {}),
                visibility,
                note_date: noteDate,
                ...(followUpId ? { follow_up_id: followUpId } : {}),
                ...(contactName.trim() ? { contact_name: contactName.trim() } : {}),
                ...(repUserId ? { user_id: repUserId } : {}),
              })}
            >
              {submitting ? 'Processing…' : 'Save & structure'}
            </button>
          </div>
        </div>
      )}

      {(saveTyped.isError || saveVoice.isError) && (
        <div className="text-crimson text-sm">
          {saveTyped.error?.response?.data?.error || saveVoice.error?.response?.data?.error || 'Save failed'}
        </div>
      )}
    </div>
  );
}

function formatDuration(s) {
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// Search-as-you-type customer combobox — live server search, covers all 3000+ customers.
function CustomerCombobox({ value, hintValue, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);

  // Load selected customer name on mount / value change
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    api.get(`/customers/${value}`).then(r => setSelected(r.data.data)).catch(() => {});
  }, [value]);

  const search = useCallback((q) => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(() => {
      api.get(`/customers?q=${encodeURIComponent(q)}`).then(r => setResults(r.data.data || [])).catch(() => {});
    }, 200);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  function pick(c) {
    onChange(c.id, c.name);
    setSelected(c);
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.blur();
  }

  function usePlaceholder() {
    onChange(null, query.trim(), true);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  function clear(e) {
    e.stopPropagation();
    onChange(null, '');
    setSelected(null);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  function handleBlur(e) {
    if (listRef.current?.contains(e.relatedTarget)) return;
    setOpen(false);
    setQuery('');
  }

  const placeholder = selected ? selected.name : hintValue ? `📝 ${hintValue}` : 'Search customer…';

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          className="input pr-7"
          placeholder={placeholder}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); search(e.target.value); }}
          onFocus={() => { if (!selected) setOpen(true); }}
          onBlur={handleBlur}
          autoComplete="off"
        />
        {(selected || hintValue) && (
          <button
            type="button"
            onMouseDown={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-crimson text-lg leading-none"
            aria-label="Clear customer"
          >
            ×
          </button>
        )}
      </div>

      {selected && !open && (
        <div className="mt-1 text-xs text-muted px-1">
          {selected.account_number && <span className="font-mono">{selected.account_number} · </span>}
          {selected.name}
          {(selected.city || selected.state) && (
            <span> · {[selected.city, selected.state].filter(Boolean).join(', ')}</span>
          )}
        </div>
      )}

      {!selected && hintValue && !open && (
        <div className="mt-1 text-xs px-1">
          <span className="text-amber-800">📝 {hintValue}</span>{' '}
          <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">not linked</span>
        </div>
      )}

      {open && results.length > 0 && (
        <ul
          ref={listRef}
          className="absolute z-50 mt-1 w-full bg-white border border-navy-100 rounded-lg shadow-panel max-h-56 overflow-y-auto"
        >
          {results.map(c => (
            <li key={c.id}>
              <button
                type="button"
                onMouseDown={() => pick(c)}
                className="w-full text-left px-3 py-2 hover:bg-panel flex items-baseline gap-2"
              >
                <span className="font-medium text-sm truncate">{c.name}</span>
                {(c.city || c.state) && (
                  <span className="text-xs text-muted truncate">{[c.city, c.state].filter(Boolean).join(', ')}</span>
                )}
                {c.account_number && (
                  <span className="text-xs text-muted font-mono shrink-0 ml-auto">{c.account_number}</span>
                )}
              </button>
            </li>
          ))}
          {query.trim() && (
            <li className="border-t border-navy-100">
              <button
                type="button"
                onMouseDown={usePlaceholder}
                className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm text-amber-900"
              >
                📝 Use "{query}" as a placeholder — not linked to a customer record
              </button>
            </li>
          )}
        </ul>
      )}

      {open && query.trim() && results.length === 0 && (
        <div ref={listRef} className="absolute z-50 mt-1 w-full bg-white border border-navy-100 rounded-lg shadow-panel">
          <div className="px-3 py-2 text-sm text-muted">No customers match "{query}"</div>
          <button
            type="button"
            onMouseDown={usePlaceholder}
            className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm text-amber-900 border-t border-navy-100"
          >
            📝 Use "{query}" as a placeholder — not linked to a customer record
          </button>
        </div>
      )}
    </div>
  );
}
